export function wrapText(text: string, limit: number): string {
    if (!text) return '';

    if (text.length > limit) {
        const firstLine = text.substring(0, limit);
        const secondLine = text.substring(limit);
        return `${firstLine}<br>${secondLine}`;
    }

    return text;
}

export function truncateText( text: string | null | undefined, limit: number ): string | null {
    if (text == null) {
        return null;
    }
    return text.length > limit ? text.substring(0, limit) + '...' : text;
}
export function normalizeList(
    value: unknown
): string[] {
    // Already an array
    if (Array.isArray(value)) {
        return value
            .map(v => String(v ?? '').trim())
            .filter(Boolean);
    }

    // Null / undefined
    if (value == null) {
        return [];
    }

    // String cases
    if (typeof value === 'string') {
        let s = value.trim();
        if (!s) return [];

        // Case 1: stringified JSON array → '["a","b"]'
        if (s.startsWith('[') && s.endsWith(']')) {
            try {
                const parsed = JSON.parse(s);
                if (Array.isArray(parsed)) {
                    return parsed
                        .map(v => String(v ?? '').trim())
                        .filter(Boolean);
                }
            } catch {
                // not valid JSON → fall through
            }

            // Case 2: pseudo-array → "[a, b, c]"
            s = s.slice(1, -1).trim();
        }

        // Case 3: comma-separated string → "a, b, c"
        return s
            .split(',')
            .map(v => v.trim())
            .filter(Boolean);
    }

    // Everything else
    return [];
}

// Shifts a UTC ISO timestamp by a number of minutes (negative = earlier), returning a
// no-milliseconds "...Z" ISO string. Used to preview a feed's window while its lead/trail minutes
// are edited (the backend stores only the minutes and computes the same window from the match):
// start = cal_match.startTime - lead, end = cal_match.endTime + trail. The trail side anchors on endTime rather than startTime
// because match duration varies too much by format for a fixed offset from kickoff to
// safely predict when a feed should come down.
export function offsetIsoMinutes(iso: string | undefined, minutes: number | undefined): string | undefined {
    if (!iso) return undefined;
    const date = new Date(iso);
    if (isNaN(date.getTime())) return undefined;
    date.setUTCMinutes(date.getUTCMinutes() + (minutes || 0));
    return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

// A source's feed window in ms, derived from its match - the only source of time (same rule as the backend's FeedWindow):
// match startTime - leadMinutes .. (match endTime, or startTime when none) + trailMinutes. null when the match is unknown.
export function feedWindowMs(
    channel: { leadMinutes?: number, trailMinutes?: number } | null | undefined,
    match: { startTime?: string, endTime?: string } | null | undefined
): { start: number, end: number } | null {
    if (!channel || !match) return null;
    const startIso = offsetIsoMinutes(match.startTime, -(channel.leadMinutes || 0));
    const endIso = offsetIsoMinutes(match.endTime || match.startTime, channel.trailMinutes || 0);
    const start = startIso ? Date.parse(startIso) : NaN;
    const end = endIso ? Date.parse(endIso) : NaN;
    return Number.isNaN(start) || Number.isNaN(end) ? null : { start, end };
}

// Inverse of offsetIsoMinutes - whole minutes from `fromIso` to `toIso` (positive when
// toIso is later). Used to redisplay a saved stream start/end as a lead/trail minute
// offset when re-opening an already-configured slot for editing.
export function minutesBetween(fromIso: string | undefined, toIso: string | undefined): number | undefined {
    if (!fromIso || !toIso) return undefined;
    const from = new Date(fromIso);
    const to = new Date(toIso);
    if (isNaN(from.getTime()) || isNaN(to.getTime())) return undefined;
    return Math.round((to.getTime() - from.getTime()) / 60000);
}

// All admin times are shown and edited in the viewer's own timezone, which comes from the
// browser/OS (Intl), not from any setting. <input type="datetime-local"> edits local wall-clock
// digits, so these convert an ISO instant to that value and back to a "...Z" instant for the BE.
const pad2 = (n: number): string => String(n).padStart(2, '0');

export function toDateTimeLocal(iso: string | undefined | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function fromDateTimeLocal(value: string | undefined | null): string | undefined {
    if (!value) return undefined;
    const d = new Date(value);
    if (isNaN(d.getTime())) return undefined;
    return d.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

// "2026-09-04 15:30" in local time.
export function formatLocalDateTime(iso: string | undefined | null): string {
    const v = toDateTimeLocal(iso);
    return v ? v.replace('T', ' ') : '';
}

// "17 Sep 05:00" in local time.
export function formatLocalDayHm(value: number | string | Date | undefined | null): string {
    if (value === undefined || value === null) return '?';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '?';
    const day = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    return `${day} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// "17 Sep 05:00–07:30" in local time ("?" when there is no end).
export function formatLocalShortRange(startIso: string | undefined | null, endIso: string | undefined | null): string {
    const start = startIso ? new Date(startIso) : null;
    if (!start || isNaN(start.getTime())) return '';
    const end = endIso ? new Date(endIso) : null;
    const endHm = end && !isNaN(end.getTime()) ? `${pad2(end.getHours())}:${pad2(end.getMinutes())}` : '?';
    return `${formatLocalDayHm(start)}–${endHm}`;
}

// "UTC+2", "UTC-5", "UTC+5:30" - the browser's current offset.
export function localTimezoneLabel(): string {
    const off = -new Date().getTimezoneOffset();
    const abs = Math.abs(off);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return `UTC${off >= 0 ? '+' : '-'}${h}${m ? ':' + pad2(m) : ''}`;
}

// ISO instant -> "YYYY-MM-DD" in the viewer's OWN local time - the ?date= param Daily.vue
// expects (Daily's day boundary is deliberately local, not UTC).
export function localDateParamFromIso(iso: string | undefined | null): string | undefined {
    if (!iso) return undefined;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return undefined;
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
}

export function formatUnixTimestampDate(timestamp: number | undefined): string | null {
    if (timestamp) {
        const date = new Date(timestamp); // Convert Unix timestamp to milliseconds
        const year = date.getUTCFullYear();
        const month = (date.getUTCMonth() + 1).toString().padStart(2, '0'); // Months are 0-based
        const day = date.getUTCDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    } else {
        return null;
    }
}

export function formatUnixTimestamp(timestamp: number | undefined): string | null {
    if (timestamp) {
        const date = new Date(timestamp);
        const year = date.getUTCFullYear();
        const month = (date.getUTCMonth() + 1).toString().padStart(2, '0'); // Months are 0-based
        const day = date.getUTCDate().toString().padStart(2, '0');
        const hours = date.getUTCHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } else {
        return null;
    }
}

// Apply-to-all only touches matches that have not started yet.
// A missing/invalid start time counts as upcoming so a half-filled match is never locked out.
export function isMatchUpcoming(startIso: string | undefined | null, nowMs: number = Date.now()): boolean {
    if (!startIso) return true;
    const start = new Date(startIso).getTime();
    return isNaN(start) || start > nowMs;
}
